import L from 'leaflet';
import { FunctionComponent, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import './map-fullscreen-control.css';

const ENTER_TITLE = 'Afficher la carte en plein écran';
const EXIT_TITLE = 'Quitter le plein écran';
// The user moves the map with them
const USER_EVENTS = ['mousedown', 'touchstart', 'wheel', 'keydown'];

// Button under the zoom ones that toggles the map in fullscreen
const MapFullscreenControl: FunctionComponent = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    let button: HTMLAnchorElement;
    let icon: HTMLElement;

    const updateButton = () => {
      const isFullscreen = document.fullscreenElement === container;
      button.title = isFullscreen ? EXIT_TITLE : ENTER_TITLE;
      button.setAttribute('aria-label', button.title);
      icon.className = isFullscreen ? 'compress icon' : 'expand icon';
    };

    // The view to keep while the map is resized, until the user moves it.
    // The window is resized several times after the fullscreen change and
    // each resize makes FitBounds fit the features again.
    let keptView: { center: L.LatLng; zoom: number } | undefined;

    const restoreView = () => {
      if (keptView) {
        map.setView(keptView.center, keptView.zoom, { animate: false });
      }
    };

    const forgetView = () => {
      keptView = undefined;
    };

    // The browser scrolls the page to the top when the map goes fullscreen:
    // the page scroll is restored when it leaves it
    let pageScroll: { x: number; y: number } | undefined;

    const restorePageScroll = () => {
      if (!pageScroll || document.fullscreenElement) return;

      const { x, y } = pageScroll;
      pageScroll = undefined;
      const scroll = () => window.scrollTo(x, y);
      scroll();
      // Again once the window has its size back, resized a bit later
      window.addEventListener('resize', scroll);
      setTimeout(() => window.removeEventListener('resize', scroll), 1000);
    };

    const onFullscreenChange = () => {
      updateButton();
      restorePageScroll();
      // The map has not been resized yet, its view is the one to keep
      keptView = { center: map.getCenter(), zoom: map.getZoom() };
      // Listened after FitBounds to restore the view after its fit
      map.off('resize', restoreView);
      map.on('resize', restoreView);
      map.invalidateSize();
    };

    const control = new L.Control({ position: 'topleft' });
    control.onAdd = () => {
      const bar = L.DomUtil.create('div', 'leaflet-bar');
      button = L.DomUtil.create('a', 'map-fullscreen-button', bar);
      button.href = '#';
      button.setAttribute('role', 'button');
      icon = L.DomUtil.create('i', '', button);
      updateButton();

      L.DomEvent.disableClickPropagation(bar);
      L.DomEvent.on(button, 'click', (event) => {
        L.DomEvent.preventDefault(event);
        // Rejected when the browser refuses it, the map then stays as is
        if (document.fullscreenElement === container) {
          document.exitFullscreen().catch(() => undefined);
        } else {
          pageScroll = { x: window.scrollX, y: window.scrollY };
          container.requestFullscreen().catch(() => {
            pageScroll = undefined;
          });
        }
      });

      return bar;
    };

    control.addTo(map);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    // Captured, the controls (zoom buttons) stop the propagation
    USER_EVENTS.forEach((type) =>
      container.addEventListener(type, forgetView, true),
    );

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      USER_EVENTS.forEach((type) =>
        container.removeEventListener(type, forgetView, true),
      );
      map.off('resize', restoreView);
      control.remove();
    };
  }, [map]);

  return null;
};

export default MapFullscreenControl;
